import { useQuery } from '@tanstack/react-query';
import { useT } from '../i18n/locale-context';
import { getTenderIntegrity } from '../lib/catalog-client';
import { useHydrated } from '../lib/use-hydrated';
import { AnalyticsTeaser } from './analytics-teaser';
import { SignalRow } from './analytics-card-parts';
import { ProcedureTeaserBody } from './analytics-teaser-bodies';
import { InfoHint } from './info-hint';

/**
 * Integrity facts for a tender's detail page — signals that a lot may be
 * tailored: re-listed from a failed prior procedure, a single registered
 * participant, an abnormal deposit vs the section median, disqualifying
 * requirements. Facts only (fact + source + n), never a "risk" label.
 *
 * Same fetch/gate/hide-when-empty pattern as {@link BuyerGraphPanel}: Pro-gated
 * data, a locked teaser for everyone else (guests included — the endpoint is
 * public and answers them with the locked payload), and nothing at all when the
 * panel would be empty (`!hasAny`).
 * Lives in the main column (not the sidebar) so the re-listing signal — the
 * strongest, and the one nobody else surfaces — leads and owns `#sec-integrity`.
 *
 * Visual: each signal is an icon-chip + fact + evidence row. Emphasis is carried
 * by the chip colour and a soft tint on notable rows (not by shouting every
 * heading red), so a mixed tender reads as a calm list where the flagged rows
 * still stand out. The re-listing row leads with the strongest tint.
 */
export function TenderIntegrityPanel({ tenderId }: { tenderId: string }) {
  const t = useT();
  // NO auth gate: the route is public since 2026-09-18 and answers a guest with
  // the LOCKED payload. A visitor who has not registered is exactly the person
  // the locked card is arguing at, so hiding it from them was backwards.
  const hydrated = useHydrated();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tender-integrity', tenderId],
    queryFn: () => getTenderIntegrity(tenderId),
    // ⚠️ BROWSER ONLY. The prerender renders this component on ~5k tender pages
    // × 2 locales; letting these three queries run there would add ~30k API
    // calls per run — including the buyer-graph union — to a 4-core host that
    // is also serving live traffic. `useHydrated()` is false during SSR and
    // during hydration, so the card is fetched after the page is interactive
    // and never by the prerenderer. (react-query does not fetch in
    // `renderToString` either; this makes that guarantee explicit and greppable
    // instead of relying on it.)
    enabled: hydrated,
  });

  /** The locked card: the two signal rows in their real shape, each finding
   *  replaced by a blurred pill, under ONE true line — how many checks this lot
   *  has inputs for. How many ran is not what they found. */
  const teaser = (note?: string) => (
    <AnalyticsTeaser
      wall="integrity"
      tenderId={tenderId}
      title={t('tenderIntegrity.title')}
      hint={t('tenderIntegrity.hint')}
      note={note}
    >
      <ProcedureTeaserBody />
    </AnalyticsTeaser>
  );

  if (isLoading) {
    return (
      <section
        id="sec-integrity"
        className="scroll-mt-16 animate-pulse rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
        aria-hidden
      >
        <div className="h-4 w-40 rounded bg-bone" />
        <div className="mt-4 h-14 rounded-lg bg-bone" />
        <div className="mt-2 h-14 rounded-lg bg-bone" />
      </section>
    );
  }

  if (isError || !data) return null;

  const it = !data.locked ? data.integrity : null;

  // No renderable facts → the upgrade CTA only when data exists behind the wall
  // (`locked && hasData`), never a dead upsell; else render nothing.
  if (!it || !it.hasAny) {
    if (!(data.locked && data.hasData !== false)) return null;
    const checks = data.locked ? data.checksCount : undefined;
    return (
      <section id="sec-integrity" className="scroll-mt-16">
        {teaser(checks ? t('tenderIntegrity.teaserBasis', { n: checks }) : undefined)}
      </section>
    );
  }

  const rel = it.relisting;
  const relSub = rel
    ? [
        rel.outcome === 'closed_no_winner' ? t('tenderIntegrity.prevFailed') : null,
        rel.participantCount != null
          ? t('tenderIntegrity.prevParticipants', { n: rel.participantCount })
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <section
      id="sec-integrity"
      className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
    >
      <div className="mb-3.5 flex items-center gap-1.5">
        <h3 className="text-[15px] font-bold text-ink">{t('tenderIntegrity.title')}</h3>
        <InfoHint text={t('tenderIntegrity.hint')} />
      </div>

      <ul className="flex flex-col gap-2">
        {rel && (
          <SignalRow
            icon="relist"
            lead
            label={t('tenderIntegrity.relisting', { id: rel.externalId })}
            sub={relSub}
          />
        )}

        {/* «Единственный участник» and the flag are verdicts on a CLOSED
            procedure; while bidding is open the count is provisional and reads
            as a plain fact (2026-09-13). The API already hides an open-lot 0. */}
        {it.participantsLive != null && (
          <SignalRow
            icon="person"
            flag={(it.participantsFinal ?? true) && it.participantsLive <= 1}
            label={
              it.participantsLive === 1 && (it.participantsFinal ?? true)
                ? t('tenderIntegrity.single')
                : t('tenderIntegrity.participants', { n: it.participantsLive })
            }
          />
        )}

        {it.deposit && (
          <SignalRow
            icon="shield"
            flag={it.deposit.elevated}
            label={t('tenderIntegrity.deposit', { pct: it.deposit.pct })}
            sub={
              it.deposit.sectionMedianPct != null
                ? t('tenderIntegrity.median', { pct: it.deposit.sectionMedianPct })
                : undefined
            }
          />
        )}

        {it.requirements && (
          <SignalRow
            icon="quals"
            flag={it.requirements.disqualifyingCount > 0}
            label={t('tenderIntegrity.quals', { n: it.requirements.disqualifyingCount })}
            sub={
              it.requirements.techWeight != null && it.requirements.costWeight != null
                ? t('tenderIntegrity.weights', {
                    tech: it.requirements.techWeight,
                    cost: it.requirements.costWeight,
                  })
                : undefined
            }
            detail={
              // The criteria themselves, collapsed: a count alone told the
              // reader nothing to act on (2026-09-13, Igor).
              (it.requirements.disqualifying?.length ?? 0) > 0 ? (
                <details className="mt-1.5">
                  <summary className="cursor-pointer text-[12.5px] font-semibold text-brand">
                    {t('tenderIntegrity.qualsShow')}
                  </summary>
                  <ol className="mt-1.5 list-decimal space-y-1 pl-4 text-[12.5px] leading-snug text-ink-soft">
                    {it.requirements.disqualifying!.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ol>
                </details>
              ) : undefined
            }
          />
        )}
      </ul>

      <p className="mt-4 border-t border-edge pt-3 text-[12px] text-ink-muted">
        {t('tenderIntegrity.source')}
      </p>
    </section>
  );
}
